"use client";

import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";

interface LocationMapProps {
  lat?: number;
  lon?: number;
  city?: string;
  country?: string;
}

export default function LocationMap({ lat, lon, city, country }: LocationMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current || typeof lat !== "number" || typeof lon !== "number") return;

    let L: typeof import("leaflet");
    let map: import("leaflet").Map;
    let mounted = true;

    const initMap = async () => {
      if (!mapRef.current) return;
      L = (await import("leaflet")).default;

      if (!mounted) return;

      map = L.map(mapRef.current, {
        zoomControl: false,
      }).setView([lat, lon], 10);

      L.tileLayer("https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", {
        attribution: "© Google Maps",
        maxZoom: 19,
      }).addTo(map);

      const customIcon = L.divIcon({
        className: "custom-marker",
        html: `
          <div style="
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            width: 30px;
            height: 30px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: 8px;
              height: 8px;
              background: white;
              border-radius: 50%;
              transform: rotate(45deg);
            "></div>
          </div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -30],
      });

      L.marker([lat, lon], { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div style="
            font-family: system-ui, -apple-system, sans-serif;
            padding: 8px;
            text-align: center;
          ">
            <div style="font-weight: bold; color: #333; margin-bottom: 4px;">
              ${city || "Location"}
            </div>
            <div style="color: #666; font-size: 12px;">
              ${country || ""}
            </div>
          </div>
        `)
        .openPopup();
    };

    void initMap();

    return () => {
      mounted = false;
      if (map) {
        map.remove();
      }
    };
  }, [lat, lon, city, country]);

  if (typeof lat !== "number" || typeof lon !== "number") {
    return (
      <div className="flex h-[400px] w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sm text-neutral-400">
        Выберите город для отображения на карте
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      className="h-[400px] w-full rounded-xl overflow-hidden border border-white/10"
      style={{ minHeight: 400 }}
    />
  );
}
